import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { Select } from "components/inputs/select/Select";
import { TextInput } from "components/inputs/text-input/TextInput";
import { Checkbox } from "components/inputs/checkbox/Checkbox";
import {
    getAssetType,
    determineFileType
} from "views/page-content/cloud/upload/upload-helpers/helpers";
import { AssetType } from "helpers/assetHelper";
import { AssetUploadThumbnail } from "components/thumbnails/AssetThumbnail/AssetUploadThumbnail";
import { AssetLayering } from "views/page-content/cloud/upload/types";
import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import { UppyFile } from "@uppy/core";
import { useProductionGroupsAssignment } from "hooks/useProductionGroupsAssignment";
import { BaseAutocomplete } from "components/inputs/autocomplete/base-autocomplete";
import { displayFileSize } from "helpers/display";

export interface AssetUploadFormProps {
    file: UppyFile;
    previewUrl?: string | null;
    fileAttributes: { [fileName: string]: any };
    setFileAttributes: Dispatch<SetStateAction<{ [fileName: string]: any }>>;
    allProductionGroups: string[];
}

export const AssetUploadForm = ({
    file,
    previewUrl,
    fileAttributes,
    setFileAttributes,
    allProductionGroups
}: AssetUploadFormProps) => {
    const { t } = useTranslation();

    const orientationOptions = [
        { value: "horizontal", text: t("cloud-assets:horizontal") },
        { value: "vertical", text: t("cloud-assets:vertical") }
    ];

    const layerOptions = [
        {
            value: AssetLayering.FullScreen,
            text: t("cloud-assets:full-screen")
        },
        { value: AssetLayering.Overlay, text: t("cloud-assets:overlay") }
    ];

    // Update production groups for this specific file
    const setAssignedProductionGroups = useCallback(
        (groups: string[]) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    productionGroups: groups
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const {
        allProductionGroupsAsObjects,
        selectedProductionGroupsAsObjects,
        handleAddProductionGroup,
        handleAssignProductionGroup,
        handleRemoveProductionGroup
    } = useProductionGroupsAssignment({
        allProductionGroups,
        onSetSelectedProductionGroups: setAssignedProductionGroups
    });

    const handleLayerOptionChange = useCallback(
        (newLayerOption: AssetLayering) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    layerOption: newLayerOption
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const handleOrientationChange = useCallback(
        (newOrientation: string) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    orientation: newOrientation
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const handleThumbnailTagChange = useCallback(
        (newThumbnailTag: string) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    thumbnailTag: newThumbnailTag
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const handleEnableAudioChange = useCallback(
        (newEnableAudio: boolean) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    enableAudio: newEnableAudio
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const handleAudioOnlyChange = useCallback(
        (newAudioOnly: boolean) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    audioOnly: newAudioOnly
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const handleEndOnBlackChange = useCallback(
        (newEndOnBlack: boolean) => {
            setFileAttributes((prev) => ({
                ...prev,
                [file.id]: {
                    ...prev[file.id],
                    endOnBlack: newEndOnBlack
                }
            }));
        },
        [file.id, setFileAttributes]
    );

    const fileType = useMemo(() => determineFileType(file), [file]);
    const assetType = useMemo(() => getAssetType(file.type), [file.type]);

    return (
        <form
            role="form"
            className={styles["asset-upload-form"]}
            key={file.id}
            aria-labelledby={`form-heading-${file.id}`}
        >
            <h6 id={`form-heading-${file.id}`}>
                {fileType.type + " " + t("cloud-assets:file-settings")}
            </h6>
            <fieldset>
                <legend className="sr-only">
                    {t("cloud-assets:file-information")}
                </legend>
                <div className={styles["file-info-row"]}>
                    <div className={styles["thumbnail-and-name"]}>
                        <AssetUploadThumbnail
                            assetFile={file}
                            selectedAspectRatio="horizontal"
                            assetLayering={AssetLayering.FullScreen}
                            previewUrl={previewUrl}
                        />
                        <p>
                            <span className={styles["file-label"]}>
                                {t("cloud-assets:file")}
                            </span>
                            : {file.name}
                        </p>
                    </div>
                    <p className={styles["file-size"]}>
                        {displayFileSize(file.size)}
                    </p>
                </div>
            </fieldset>

            <fieldset>
                <legend className="sr-only">
                    {t("cloud-assets:production-groups")}
                </legend>
                <BaseAutocomplete
                    id={file.id}
                    assignedItems={selectedProductionGroupsAsObjects}
                    handleAddItem={handleAddProductionGroup}
                    handleAssignItem={handleAssignProductionGroup}
                    handleRemoveItem={handleRemoveProductionGroup}
                    itemIdKey="Id"
                    itemLabelKey="Name"
                    items={allProductionGroupsAsObjects}
                    variant="add"
                    labels={{
                        appliedItemsLabel: t("cloud-assets:production-group"),
                        noItemsAppliedLabel: t(
                            "cloud-assets:no-production-groups"
                        ),
                        inputLabel: t("cloud-assets:new-group"),
                        addLabel: t("cloud-assets:add")
                    }}
                />
            </fieldset>

            <fieldset>
                <legend className="sr-only">
                    {t("cloud-assets:file-settings")}
                </legend>
                <div className={styles["file-attribute-inputs"]}>
                    {assetType === AssetType.image && (
                        <Select
                            id="layer-option"
                            onChange={(e) => {
                                handleLayerOptionChange(
                                    e.target.value as AssetLayering
                                );
                            }}
                            selected={fileAttributes[file.id]?.layerOption}
                            options={layerOptions}
                            label="Layer Option"
                        />
                    )}
                    {(assetType === AssetType.video ||
                        assetType === AssetType.image) && (
                        <Select
                            id="image-orientation"
                            onChange={(e) =>
                                handleOrientationChange(e.target.value)
                            }
                            selected={fileAttributes[file.id]?.orientation}
                            options={orientationOptions}
                            label={t("cloud-assets:image-orientation")}
                        />
                    )}
                    {assetType === AssetType.audio && (
                        <TextInput
                            id="thumbnail-tag-input"
                            onChange={(e) =>
                                handleThumbnailTagChange(e.target.value)
                            }
                            type="text"
                            value={fileAttributes[file.id]?.thumbnailTag}
                            label={t("cloud-assets:thumbnail-tag")}
                            className={"thumbnail-tag-input"}
                        />
                    )}
                </div>
                {assetType === AssetType.video && (
                    <div className={styles["video-file-attribute-checkboxes"]}>
                        <div className={styles["column"]}>
                            <Checkbox
                                id={`enable-audio-${file.id}`}
                                label={t("cloud-assets:enable-audio")}
                                onChange={(e) =>
                                    handleEnableAudioChange(e.target.checked)
                                }
                                checked={fileAttributes[file.id]?.enableAudio}
                                customClass={styles["enable-audio"]}
                            />
                            <Checkbox
                                id={`audio-only-${file.id}`}
                                label={t("cloud-assets:audio-only")}
                                onChange={(e) =>
                                    handleAudioOnlyChange(e.target.checked)
                                }
                                checked={fileAttributes[file.id]?.audioOnly}
                                customClass={styles["audio-only"]}
                            />
                        </div>
                        <div className={styles["column"]}>
                            <Checkbox
                                id={`end-on-black-${file.id}`}
                                label={t("cloud-assets:end-on-black")}
                                onChange={(e) =>
                                    handleEndOnBlackChange(e.target.checked)
                                }
                                checked={fileAttributes[file.id]?.endOnBlack}
                                customClass={styles["end-on-black"]}
                            />
                        </div>
                    </div>
                )}
            </fieldset>
        </form>
    );
};
